/**
 * TemplateCompose — FIKET-156 S4-b 템플릿 조립 페이지
 *
 * 좌: shell 슬롯을 라이브러리 섹션으로 채움 + 전역 CSS/JS 시트 선택.
 * 우: 저장 전 드라이런 미리보기 iframe. 서버가 실제 발행과 같은 순서로 CSS/JS를 주입하고
 *     샘플 청첩장 데이터로 Liquid를 렌더한 완결 문서를 준다(섹션 라이브러리와 동일 파이프라인).
 *     섹션 라이브러리와 같은 모바일 폭 프레임에서 가운데 정렬하고, 선택이 바뀌면
 *     디바운스 후 자동으로 다시 렌더한다(수동 갱신 버튼 없음).
 * 저장 시 usesComposition=true로 승격되고 기존 liquidHtml을 덮어쓴다.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button, Spinner, Alert, ConfirmDialog, EmptyState } from '@/ui'
import { SlotList } from './template-compose/SlotList'
import { GlobalSheetSelect } from './template-compose/GlobalSheetSelect'
import type { CompositionData, SlotSelection, CompositionRequest } from './template-compose/types'
import styles from './TemplateCompose.module.css'

export function TemplateCompose() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { addToast } = useToast()

  const { data, error, isLoading } = useApiFetch<CompositionData>(
    id ? `/api/admin/templates/${id}/composition` : null,
  )

  const [selection, setSelection] = useState<SlotSelection>({})
  const [stylesheetId, setStylesheetId] = useState<string | null>(null)
  const [scriptId, setScriptId] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // 진입 데이터 → 초기 선택 상태 (템플릿당 1회만 시딩)
  // 재요청(StrictMode 이중 마운트·refetch 등)으로 data 참조가 새로 와도 이미 시딩했으면
  // 덮어쓰지 않는다 — 늦게 도착한 응답이 사용자 편집을 되돌리는 것을 막는다.
  const seededIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!data || !id) return
    if (seededIdRef.current === id) return
    seededIdRef.current = id
    const init: SlotSelection = {}
    for (const slot of data.slots) {
      init[slot.sectionType] = slot.currentSectionDesignId ?? ''
    }
    setSelection(init)
    setStylesheetId(data.stylesheetId)
    setScriptId(data.scriptId)
  }, [data, id])

  // 모든 슬롯은 비워둘 수 있다(필수 섹션 없음) — 비운 슬롯은 결과에서 그 섹션이 빠질 뿐이다.
  // 저장/미리보기를 막는 조건은 "조립할 슬롯이 아예 없는 템플릿"뿐이다.
  const emptySlots = useMemo(
    () => (data?.slots ?? []).filter((s) => !selection[s.sectionType]),
    [data, selection],
  )
  const canCompose = (data?.slots.length ?? 0) > 0

  const buildRequest = useCallback((): CompositionRequest => {
    const slots = data?.slots ?? []
    return {
      sections: slots.map((slot) => ({
        sectionDesignId: selection[slot.sectionType],
        sectionType: slot.sectionType,
        sortOrder: slot.sortOrder,
      })),
      stylesheetId,
      scriptId,
    }
  }, [data, selection, stylesheetId, scriptId])

  // ── 미리보기 자동 갱신 (진입 즉시 1회 + 선택 변경 시 디바운스) ──
  // 늦게 도착한 응답이 최신 선택의 결과를 덮어쓰지 않도록 요청 순번으로 가드한다.
  const previewSeqRef = useRef(0)
  useEffect(() => {
    if (!id || !canCompose) {
      // 순번을 올려 진행 중인 이전 요청의 응답을 무효화한다(빈 상태를 덮어쓰지 않게)
      previewSeqRef.current++
      setPreviewHtml(null)
      setPreviewError(null)
      setPreviewing(false)
      return
    }
    const seq = ++previewSeqRef.current
    setPreviewing(true)
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch<{ renderedHtml: string }>(
          `/api/admin/templates/${id}/composition/preview`,
          { method: 'POST', body: JSON.stringify(buildRequest()) },
        )
        if (seq !== previewSeqRef.current) return
        // 서버가 CSS/JS 주입 + 샘플 데이터 Liquid 렌더까지 마친 완결 문서를 준다
        setPreviewHtml(res.renderedHtml)
        setPreviewError(null)
      } catch (e: unknown) {
        if (seq !== previewSeqRef.current) return
        setPreviewError(e instanceof Error ? e.message : '미리보기 실패')
        setPreviewHtml(null)
      } finally {
        if (seq === previewSeqRef.current) setPreviewing(false)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [id, canCompose, buildRequest])

  const handleSave = useCallback(async () => {
    setConfirmOpen(false)
    if (!canCompose) {
      addToast({ type: 'error', message: '조립할 슬롯이 없습니다' })
      return
    }
    setSaving(true)
    try {
      await apiFetch(`/api/admin/templates/${id}/composition`, {
        method: 'PUT',
        body: JSON.stringify(buildRequest()),
      })
      addToast({ type: 'success', message: '조립 저장 완료 — 조립 모델로 전환됨' })
    } catch (e: any) {
      addToast({ type: 'error', message: e.message || '저장 실패' })
    } finally {
      setSaving(false)
    }
  }, [canCompose, id, buildRequest, addToast])

  const handleSlotChange = useCallback((sectionType: string, sectionDesignId: string) => {
    setSelection((prev) => ({ ...prev, [sectionType]: sectionDesignId }))
  }, [])

  if (isLoading) return <div className={styles.loadingFull}><Spinner /></div>

  if (error || !data) {
    return (
      <div className={styles.page}>
        <EmptyState
          title="조립할 수 없는 템플릿입니다"
          description={error ?? 'compositionShell이 없습니다. 시드가 필요합니다.'}
          action={<Button variant="outline" onClick={() => navigate('/wedding/templates')}>템플릿 목록으로</Button>}
        />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>템플릿 조립</h2>
        <div className={styles.headerActions}>
          <Button onClick={() => setConfirmOpen(true)} disabled={!canCompose} loading={saving}>
            조립 저장
          </Button>
        </div>
      </div>

      <div className={styles.main}>
        <div className={styles.leftPane}>
          <div className={styles.sheetSelects}>
            <GlobalSheetSelect
              label="전역 CSS 시트"
              value={stylesheetId}
              sheets={data.stylesheets.filter((s) => s.type === 'css')}
              onChange={setStylesheetId}
            />
            <GlobalSheetSelect
              label="전역 JS 시트"
              value={scriptId}
              sheets={data.stylesheets.filter((s) => s.type === 'js' || s.type === 'html')}
              onChange={setScriptId}
            />
          </div>
          {/* 슬롯을 비운 것은 정상 상태 — 결과에 그 섹션이 없다는 사실만 알린다 */}
          {emptySlots.length > 0 && (
            <Alert variant="info">
              비워둔 슬롯 {emptySlots.length}개 ({emptySlots.map((s) => s.sectionType).join(', ')})
              — 조립 결과에서 제외됩니다.
            </Alert>
          )}
          <SlotList
            slots={data.slots}
            availableSections={data.availableSections}
            selection={selection}
            onChange={handleSlotChange}
          />
        </div>

        <div className={styles.rightPane}>
          {/* 로딩 표시는 라벨 줄에만 둔다 — 미리보기 화면 자체는 건드리지 않아
              섹션을 바꿀 때 이전 결과가 그대로 남고 깜빡이지 않는다 */}
          <div className={styles.previewLabel}>
            <span className={styles.previewLabelText}>미리보기</span>
            {previewing && (
              <span className={styles.previewStatus}>
                <Spinner size="sm" /> 갱신 중...
              </span>
            )}
            <span className={styles.previewLabelHint}>샘플 데이터 렌더 · 저장 전 드라이런</span>
          </div>
          <div className={styles.previewFrame}>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} title="조립 미리보기" className={styles.previewIframe} />
            ) : previewError ? (
              <div className={styles.previewError}>
                <div className={styles.previewErrorTitle}>미리보기 실패</div>
                <pre className={styles.previewErrorPre}>{previewError}</pre>
              </div>
            ) : (
              <div className={styles.previewCenter}>
                {previewing ? '미리보기를 준비하고 있습니다' : '조립할 슬롯이 없습니다'}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSave}
        title="조립 저장"
        message={
          emptySlots.length > 0
            ? `저장하면 이 템플릿은 조립 모델로 전환되고 기존 liquidHtml을 덮어씁니다. 비워둔 슬롯 ${emptySlots.length}개(${emptySlots.map((s) => s.sectionType).join(', ')})는 결과에서 제외됩니다. 계속할까요?`
            : '저장하면 이 템플릿은 조립 모델로 전환되고 기존 liquidHtml을 덮어씁니다. 계속할까요?'
        }
        confirmLabel="저장"
        loading={saving}
      />
    </div>
  )
}
